import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Site, SiteInsert } from '@/types/site';
import { SiteMap } from '@/components/sites/SiteMap';
import { AddressAutocomplete } from '@/components/sites/AddressAutocomplete';
import { MapPin, Shield, Info, Clock } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { COUNTRIES } from '@/config/countries';
import { TIMEZONES } from '@/config/timezones';
import { UserSelector } from '@/components/shared/UserSelector';

const siteSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  site_code: z.coerce.number().min(1).max(99999999).optional(),
  street: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().length(2, 'Country must be 2-letter ISO code').optional(),
  timezone: z.string().optional(),
  criticality: z.enum(['Very Low', 'Low', 'Medium', 'High', 'Very High']).optional(),
  notes: z.string().optional(),
  owner_id: z.string().optional(),
  lat: z.number().optional(),
  lon: z.number().optional(),
  physical_security: z.enum(['Basic', 'Controlled', 'Restricted']).optional(),
  operating_start_time: z.string().optional(),
  operating_end_time: z.string().optional(),
  operating_weekdays: z.array(z.string()).optional(),
});

type SiteFormData = z.infer<typeof siteSchema>;

interface SiteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SiteInsert) => Promise<void>;
  site?: Site | null;
}

export function SiteFormDialog({ open, onOpenChange, onSubmit, site }: SiteFormDialogProps) {
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [ownerId, setOwnerId] = useState<string | undefined>(site?.owner_id);
  
  const form = useForm({
    resolver: zodResolver(siteSchema),
    defaultValues: {},
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = form;

  React.useEffect(() => {
    if (site) {
      setOwnerId(site.owner_id);
      reset({
        name: site.name,
        site_code: site.site_code ? Number(site.site_code) : undefined,
        street: site.metadata?.structured_address?.street || '',
        postal_code: site.metadata?.structured_address?.postal_code || '',
        city: site.metadata?.structured_address?.city || '',
        country: site.country || '',
        timezone: site.timezone || 'Europe/Stockholm',
        criticality: site.criticality || undefined,
        notes: site.notes || '',
        lat: site.metadata?.coordinates?.lat,
        lon: site.metadata?.coordinates?.lon,
        physical_security: site.metadata?.physical_security || undefined,
        operating_start_time: site.metadata?.operating_hours?.start_time || '07:00',
        operating_end_time: site.metadata?.operating_hours?.end_time || '16:00',
        operating_weekdays: site.metadata?.operating_hours?.weekdays || ['mon', 'tue', 'wed', 'thu', 'fri'],
      });
    } else {
      setOwnerId(undefined);
      reset({
        timezone: 'Europe/Stockholm',
        operating_start_time: '07:00',
        operating_end_time: '16:00',
        operating_weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      });
    }
  }, [site, reset]);

  const onFormSubmit = async (data: SiteFormData) => {
    try {
      // Combine structured address fields for backward compatibility
      const addressParts = [data.street, data.postal_code, data.city].filter(Boolean);
      const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;

      const metadata: any = {};

      // Store structured address in metadata
      if (data.street || data.postal_code || data.city) {
        metadata.structured_address = {
          street: data.street || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
        };
      }

      if (data.lat !== undefined && data.lon !== undefined) {
        metadata.coordinates = {
          lat: data.lat,
          lon: data.lon,
        };
      }

      if (data.physical_security) {
        metadata.physical_security = data.physical_security;
      }

      if (data.operating_start_time && data.operating_end_time) {
        metadata.operating_hours = {
          start_time: data.operating_start_time,
          end_time: data.operating_end_time,
          weekdays: data.operating_weekdays || [],
        };
      }

      const siteData: SiteInsert = {
        name: data.name,
        site_code: data.site_code ? String(data.site_code) : undefined,
        address: fullAddress,
        country: data.country,
        timezone: data.timezone || 'Europe/Stockholm',
        criticality: data.criticality,
        owner_id: ownerId,
        notes: data.notes,
        org_id: '', // Will be set by the store
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      };

      await onSubmit(siteData);
    } catch (error) {
      console.error('Error submitting site form:', error);
    }
  };

  const handleMapPick = (coords: { lat: number; lon: number }) => {
    setValue('lat', coords.lat);
    setValue('lon', coords.lon);
    setShowMapPicker(false);
  };

  const handleOpenMapPicker = () => {
    setShowMapPicker(true);
  };

  const selectedWeekdays = watch('operating_weekdays') || [];

  const toggleWeekday = (day: string) => {
    const current = selectedWeekdays;
    if (current.includes(day)) {
      setValue('operating_weekdays', current.filter(d => d !== day));
    } else {
      setValue('operating_weekdays', [...current, day]);
    }
  };

  const weekdays = [
    { value: 'mon', label: 'Mon' },
    { value: 'tue', label: 'Tue' },
    { value: 'wed', label: 'Wed' },
    { value: 'thu', label: 'Thu' },
    { value: 'fri', label: 'Fri' },
    { value: 'sat', label: 'Sat' },
    { value: 'sun', label: 'Sun' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{site ? 'Edit Site' : 'Create New Site'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Info className="h-5 w-5" />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" {...register('name')} placeholder="e.g., Headquarters" />
                {errors.name && <p className="text-sm text-danger">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="site_code">Site Number</Label>
                <Input 
                  id="site_code" 
                  type="number"
                  min="1"
                  max="99999999"
                  {...register('site_code', { valueAsNumber: true })} 
                  placeholder="e.g., 1001" 
                />
                {errors.site_code && <p className="text-sm text-danger">{errors.site_code.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="criticality">Criticality</Label>
                <Select onValueChange={(value) => setValue('criticality', value as any)} value={watch('criticality')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select criticality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Very Low">Very Low</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Very High">Very High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select 
                  onValueChange={(value) => setValue('timezone', value)} 
                  value={watch('timezone')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Physical Location
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="street">Street Address</Label>
                <AddressAutocomplete
                  value={watch('street') || ''}
                  onChange={(value) => setValue('street', value)}
                  onSelect={(address) => {
                    setValue('street', address.street);
                    setValue('postal_code', address.postal_code);
                    setValue('city', address.city);
                    setValue('country', address.country);
                    setValue('lat', address.lat);
                    setValue('lon', address.lon);
                  }}
                  placeholder="e.g., Badstigen 2"
                />
                <p className="text-xs text-muted-foreground">
                  Start typing to see address suggestions
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input id="postal_code" {...register('postal_code')} placeholder="e.g., 153 91" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input id="city" {...register('city')} placeholder="e.g., Järna" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select 
                  onValueChange={(value) => setValue('country', value)} 
                  value={watch('country')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name} ({country.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.country && (
                  <p className="text-sm text-danger">{errors.country.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>GPS Coordinates</Label>
                <div className="flex gap-2">
                  <Input 
                    type="number" 
                    step="any"
                    placeholder="Latitude" 
                    value={watch('lat') || ''} 
                    onChange={(e) => setValue('lat', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                  <Input 
                    type="number" 
                    step="any"
                    placeholder="Longitude" 
                    value={watch('lon') || ''} 
                    onChange={(e) => setValue('lon', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleOpenMapPicker} className="w-full mt-2">
                  <MapPin className="h-4 w-4 mr-2" />
                  Pick from Map
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security & Operations
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setValue('operating_start_time', '00:00');
                  setValue('operating_end_time', '23:59');
                  setValue('operating_weekdays', ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
                }}
                className="h-8 text-xs"
              >
                <Clock className="h-3 w-3 mr-1" />
                24/7 Operation
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="physical_security">Physical Security Level</Label>
                <Select onValueChange={(value) => setValue('physical_security', value as any)} value={watch('physical_security')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select security level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Controlled">Controlled</SelectItem>
                    <SelectItem value="Restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Operating Hours</Label>
                <div className="flex gap-2">
                  <Input 
                    type="time" 
                    {...register('operating_start_time')}
                    placeholder="Start" 
                  />
                  <Input 
                    type="time" 
                    {...register('operating_end_time')}
                    placeholder="End" 
                  />
                </div>
              </div>

              <div className="space-y-2 col-span-2">
                <Label>Operating Days</Label>
                <div className="flex gap-2">
                  {weekdays.map(day => (
                    <div key={day.value} className="flex items-center space-x-2">
                      <Checkbox 
                        id={day.value}
                        checked={selectedWeekdays.includes(day.value)}
                        onCheckedChange={() => toggleWeekday(day.value)}
                      />
                      <Label htmlFor={day.value} className="cursor-pointer">{day.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Asset Ownership</h3>
            <UserSelector
              value={ownerId}
              onChange={setOwnerId}
              label="Primary Owner"
              placeholder="Select an owner"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" {...register('notes')} placeholder="Additional notes..." rows={3} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : site ? 'Update Site' : 'Create Site'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {showMapPicker && (
        <Dialog open={showMapPicker} onOpenChange={setShowMapPicker}>
          <DialogContent className="max-w-4xl max-h-[90vh] z-[100]">
            <DialogHeader>
              <DialogTitle>Pick Location on Map</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Drag the marker to select coordinates, or click anywhere on the map
              </p>
            </DialogHeader>
            <div className="h-[500px] w-full border rounded-md overflow-hidden">
              <SiteMap 
                sites={watch('lat') && watch('lon') ? [{
                  id: 'temp',
                  org_id: '',
                  name: watch('name') || 'Current Location',
                  created_at: '',
                  updated_at: '',
                  metadata: {
                    coordinates: {
                      lat: watch('lat')!,
                      lon: watch('lon')!,
                    }
                  }
                } as any] : []}
                onMapClick={handleMapPick}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Current: Lat {watch('lat')?.toFixed(6) || 'N/A'}, Lon {watch('lon')?.toFixed(6) || 'N/A'}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMapPicker(false)}>
                Cancel
              </Button>
              <Button onClick={() => setShowMapPicker(false)}>
                Confirm Location
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
